import { Text } from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { EmailField } from "@src/appV2/lib/FormFields/EmailField/EmailField";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { emailFormSchema } from "@src/appV2/redesign/Profile/types/emailFormSchema";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";

interface VerifyEmailStepProps {
  onVerified: () => void;
}

export function VerifyEmailStep({ onVerified }: VerifyEmailStepProps) {
  const worker = useDefinedWorker();
  const { showErrorToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);

  const formMethods = useForm({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(emailFormSchema),
    mode: "onBlur",
  });

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting, isValid },
  } = formMethods;

  const onSubmit = async (formData: { email: string }) => {
    // Verify that the entered email matches the worker's current email
    if (formData.email.toLowerCase() !== worker.email?.toLowerCase()) {
      setError("email", {
        message:
          "The email you typed doesn't match our current records. Please make sure to input the same one you use for your account today.",
      });
      showErrorToast("Your email doesn't match");
      return;
    }

    onVerified();
  };

  return (
    <FormProvider {...formMethods}>
      <Stack
        ref={formRef}
        component="form"
        sx={{ paddingY: 4, gap: 12 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack spacing={6} marginBottom={12}>
          <Text variant="body2">
            To change your phone number, we need to verify your email first for security purposes.
          </Text>
          <EmailField name="email" showLabel={false} />
        </Stack>
        <Stack spacing={2} sx={{ marginTop: 12 }}>
          <LoadingButton
            fullWidth
            type="submit"
            variant="contained"
            isLoading={isSubmitting}
            disabled={!isValid}
          >
            Continue
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
